import { useEffect, useMemo, useState } from 'react';
import type { Material } from '@obraplan/core/domain/schedule/material';
import type { UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import type { ItemTarget } from './tree-model';

export interface ItemSubmit {
  readonly materialId: string;
  readonly quantity: number;
  readonly unitId: string;
  readonly requiredDate: string;
  readonly plannedStart: string | undefined;
  readonly plannedEnd: string | undefined;
  readonly notes: string | undefined;
}

/** Aceita "1.250,5" e "1250.5" — quem digita na obra usa vírgula. */
function parseQuantity(text: string): number {
  const normalized = text.trim().replace(/\./g, '').replace(',', '.');
  return Number(normalized);
}

export interface ItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rótulo da frente: `"Alvenaria · 3º Pavimento"`. */
  activityLabel: string;
  /** Ausente = novo item; presente = edição. */
  target: ItemTarget | undefined;
  materials: readonly Material[];
  units: readonly UnitOfMeasure[];
  onSubmit: (input: ItemSubmit) => void;
  onCreateMaterial: () => void;
  isPending: boolean;
  errorMessage: string | undefined;
}

/**
 * Item da atividade — material, quantidade e data.
 *
 * O material sai do catálogo, nunca de texto livre (RN-010): sem isso, "Tijolo 19x30" e
 * "tijolo 19 x 30" viram duas necessidades e suprimentos compra duas vezes. Quando o
 * material não existe, o caminho é criá-lo no catálogo — o botão ao lado da lista —, e
 * não digitar por cima.
 *
 * Na edição o material fica fixo: ele é metade da identidade do item (RN-024). Trocar de
 * material é remover e lançar outro.
 *
 * A unidade vem preenchida com a do material e pode ser trocada; divergir dela é
 * legítimo e vira aviso depois de salvar (RN-012), nunca bloqueio.
 */
export function ItemDialog({
  open,
  onOpenChange,
  activityLabel,
  target,
  materials,
  units,
  onSubmit,
  onCreateMaterial,
  isPending,
  errorMessage,
}: ItemDialogProps) {
  const [materialId, setMaterialId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitId, setUnitId] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedEnd, setPlannedEnd] = useState('');
  const [notes, setNotes] = useState('');

  const isEdit = target !== undefined;

  useEffect(() => {
    if (!open) return;
    setMaterialId(target?.materialId ?? materials[0]?.id ?? '');
    setQuantity(target === undefined ? '' : String(target.quantity).replace('.', ','));
    setUnitId(target?.unitId ?? '');
    setRequiredDate(target?.requiredDate ?? '');
    setPlannedStart(target?.plannedStart ?? '');
    setPlannedEnd(target?.plannedEnd ?? '');
    setNotes(target?.notes ?? '');
  }, [open, target, materials]);

  const selectedMaterial = useMemo(
    () => materials.find((material) => material.id === materialId),
    [materials, materialId],
  );

  // Trocar de material propõe a unidade padrão dele; a escolha explícita prevalece.
  useEffect(() => {
    if (isEdit || selectedMaterial === undefined) return;
    setUnitId(selectedMaterial.unitId);
  }, [isEdit, selectedMaterial]);

  const parsedQuantity = parseQuantity(quantity);
  const quantityInvalid =
    quantity.trim() !== '' && (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0);
  const periodInvalid = plannedStart !== '' && plannedEnd !== '' && plannedEnd < plannedStart;
  const canSubmit =
    materialId !== '' &&
    unitId !== '' &&
    requiredDate !== '' &&
    Number.isFinite(parsedQuantity) &&
    parsedQuantity > 0 &&
    !periodInvalid;

  const standardUnit = units.find((unit) => unit.id === selectedMaterial?.unitId);
  const chosenUnit = units.find((unit) => unit.id === unitId);
  const unitDiverges =
    standardUnit !== undefined && chosenUnit !== undefined && standardUnit.id !== chosenUnit.id;
  const materialLate = requiredDate !== '' && plannedStart !== '' && requiredDate > plannedStart;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar item' : 'Novo item'}</DialogTitle>
          <DialogDescription>
            Em {activityLabel}. O item é material, quantidade e a data em que ele precisa estar na
            obra.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              materialId,
              quantity: parsedQuantity,
              unitId,
              requiredDate,
              plannedStart: plannedStart === '' ? undefined : plannedStart,
              plannedEnd: plannedEnd === '' ? undefined : plannedEnd,
              notes: notes.trim() === '' ? undefined : notes.trim(),
            });
          }}
        >
          <Field
            label="Material"
            htmlFor="item-material"
            required
            hint={
              isEdit
                ? 'O material não muda: para trocá-lo, remova este item e lance outro.'
                : undefined
            }
          >
            <div className="flex gap-2">
              <select
                id="item-material"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm disabled:opacity-50"
                value={materialId}
                disabled={isEdit}
                onChange={(event) => setMaterialId(event.target.value)}
              >
                {materials.length === 0 && <option value="">Nenhum material no catálogo</option>}
                {materials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.name} ({material.code})
                  </option>
                ))}
              </select>
              {!isEdit && (
                <Button type="button" variant="secondary" onClick={onCreateMaterial}>
                  Novo
                </Button>
              )}
            </div>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Quantidade"
              htmlFor="item-quantity"
              required
              error={quantityInvalid ? 'A quantidade deve ser maior que zero.' : undefined}
              hint={
                chosenUnit === undefined
                  ? undefined
                  : chosenUnit.decimals === 0
                    ? 'Esta unidade não aceita casas decimais.'
                    : `Até ${String(chosenUnit.decimals)} casas decimais.`
              }
            >
              <Input
                id="item-quantity"
                autoFocus
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                placeholder="120"
              />
            </Field>

            <Field label="Unidade" htmlFor="item-unit" required>
              <select
                id="item-unit"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field
            label="Necessário na obra em"
            htmlFor="item-required"
            required
            hint="Data em que o material precisa estar na obra — não a data da compra."
          >
            <Input
              id="item-required"
              type="date"
              value={requiredDate}
              onChange={(event) => setRequiredDate(event.target.value)}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Início previsto da frente" htmlFor="item-start">
              <Input
                id="item-start"
                type="date"
                value={plannedStart}
                onChange={(event) => setPlannedStart(event.target.value)}
              />
            </Field>
            <Field
              label="Término previsto"
              htmlFor="item-end"
              error={periodInvalid ? 'O término não pode ser anterior ao início.' : undefined}
            >
              <Input
                id="item-end"
                type="date"
                value={plannedEnd}
                onChange={(event) => setPlannedEnd(event.target.value)}
              />
            </Field>
          </div>

          <Field label="Observação" htmlFor="item-notes">
            <Textarea
              id="item-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Opcional — o que quem for comprar precisa saber."
            />
          </Field>

          {/* Avisos: mostram, não impedem. Quem decide é gente (RN-012, RN-023). */}
          {(unitDiverges || materialLate) && (
            <div className="flex flex-col gap-1 rounded-md border border-(--color-state-warn) bg-(--color-surface-muted) p-3 text-sm">
              {unitDiverges && standardUnit !== undefined && chosenUnit !== undefined && (
                <p>
                  Lançado em <strong>{chosenUnit.name}</strong>; a unidade padrão do material é{' '}
                  <strong>{standardUnit.name}</strong>. Nada é convertido automaticamente.
                </p>
              )}
              {materialLate && <p>O material chega depois do início previsto da frente.</p>}
            </div>
          )}

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || !canSubmit}>
              {isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Lançar item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Cadastro rápido de material, aberto de dentro do lançamento do item. */
export function CreateMaterialDialog({
  open,
  onOpenChange,
  units,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  units: readonly UnitOfMeasure[];
  onSubmit: (input: {
    code: string;
    name: string;
    unitId: string;
    category: string | undefined;
  }) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [unitId, setUnitId] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    if (!open) return;
    setCode('');
    setName('');
    setUnitId(units[0]?.id ?? '');
    setCategory('');
  }, [open, units]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo material</DialogTitle>
          <DialogDescription>
            O catálogo é compartilhado por todas as obras: material é insumo de mercado. A
            quantidade e a data é que são da obra.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              code,
              name,
              unitId,
              category: category.trim() === '' ? undefined : category.trim(),
            });
          }}
        >
          <Field
            label="Código"
            htmlFor="material-code"
            required
            error={errorMessage}
            hint="Único no sistema — ex.: TIJ-19X30."
          >
            <Input
              id="material-code"
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>

          <Field label="Nome" htmlFor="material-name" required>
            <Input
              id="material-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Bloco cerâmico 19x30"
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Unidade padrão" htmlFor="material-unit" required>
              <select
                id="material-unit"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Categoria" htmlFor="material-category">
              <Input
                id="material-category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                placeholder="Alvenaria"
              />
            </Field>
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || code.trim() === '' || name.trim() === '' || unitId === ''}
            >
              {isPending ? 'Criando…' : 'Criar material'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
